import { parseCssColorToRgb } from './colorUtils';

/**
 * WEB-1859 — ЕДИНСТВЕННОЕ МЕСТО, ГДЕ ЖИВЁТ ПОРОГ НЕПРОЗРАЧНОСТИ ПОДЛОЖКИ.
 *
 * ЗАЧЕМ. Вопрос «достаточно ли непрозрачна подложка, чтобы считать по ней цвет
 * текста» задают шесть помощников: разметка экрана (resolveScreenLayout),
 * подпись-пилюля на фото (captionChipAppearance), плашка сообщений экрана
 * оплаты (payErrorAppearance), строка варианта ответа (optionRowStyle),
 * кнопка и значок гарантии (blockBackgroundStyle), поверхность элемента
 * (blockInnerSurface). До этой карточки ответов было ТРИ: 0.9 у экрана, 0x80 у
 * пилюли и плашки, «всегда считаем» у остальных. На одной и той же плёнке
 * текст блока выходил тёмным, а надпись кнопки — светлой.
 *
 * Это класс дефекта «у правила два конца, а правят один» (инвентарь WEB-1844,
 * backend/src/shared/guards/two-ended-rules.inventory.md). Поэтому порог не
 * просто сведён в одно число, но и закрыт двусторонним сторожем
 * `__tests__/surfaceOpacityEnds.web1859.test.ts`: множество мест, где вердикт
 * выносится, выводится ОБХОДОМ ИСХОДНИКОВ и обязано совпадать с множеством
 * потребителей этого модуля.
 *
 * ПОЧЕМУ ИМЕННО 0.9, а не половина. Порог отвечает не «тёмная ли заливка», а
 * «можно ли вообще судить о том, что окажется под текстом». Сквозь плёнку
 * видна страница (или фотография), и яркость самой плёнки её не предсказывает:
 * чёрный на 25% поверх белой страницы — СВЕТЛАЯ поверхность, а половинный
 * порог назвал бы такую заливку основанием для белого текста. Значение взято у
 * помощника экрана (resolveScreenLayout, WEB-1805): там оно уже было выведено
 * и записано словами, и это единственный из трёх ответов с обоснованием.
 * Направление ошибки у него безопасное: помощник МОЖЕТ не перекрасить текст
 * там, где перекраска была бы уместна (останется прежний вид, читаемый), но
 * никогда не перекрасит наугад по плёнке.
 *
 * ЧТО ЭТО МЕНЯЕТ У АВТОРА (осознанная цена, приёмке смотреть сюда):
 *  · заливка с непрозрачностью 0.5-0.9 у пилюли, плашки ошибки и варианта
 *    всплывающего вопроса на экране загрузки больше не включает авто-контраст
 *    — остаётся прежний вид этих элементов;
 *  · «Баннер срочности» с полупрозрачной авторской заливкой держит
 *    исторический белый текст, а не считает его по плёнке;
 *  · плотность пресета («полупрозрачная» 0.6, «размытие» 0.35, «стекло» 0.25)
 *    теперь ВХОДИТ в замер у кнопки, значка и поверхности элемента: по такой
 *    подложке цвет текста не считается, его решает тема — которая, в отличие
 *    от плёнки, знает страницу под ней;
 *  · непрозрачные подложки (сплошной цвет, пресет `solid`) считаются ровно
 *    как раньше — ни один такой блок не сдвигается.
 */

/**
 * Ниже этой непрозрачности подложка считается ПЛЁНКОЙ: судить по ней о цвете
 * текста нечем. Порог грубый намеренно — точный ответ требует знать, что лежит
 * под плёнкой, а на этом слое подложки нет.
 */
export const OPAQUE_ENOUGH_ALPHA = 0.9;

/** Непрозрачна ли подложка настолько, чтобы считать по ней цвет текста. */
export function isOpaqueEnough(alpha: number): boolean {
  return Number.isFinite(alpha) && alpha >= OPAQUE_ENOUGH_ALPHA;
}

/** Доля 0..1 из поля плотности; мусор и отсутствие — «плотность не задана». */
function densityOf(opacity: number | undefined): number {
  if (typeof opacity !== 'number' || !Number.isFinite(opacity)) return 1;
  return Math.min(1, Math.max(0, opacity));
}

/**
 * Код цвета БЕЗ альфа-канала, пригодный для `pickOnPrimaryColor`, — либо null,
 * если по этой подложке цвет текста считать нельзя.
 *
 * null возвращается в трёх случаях, и все три означают одно: «мерить нечем».
 *  1. пусто / не строка — подложки нет;
 *  2. значение неизмеримо (`var(--…)`, незнакомое имя цвета) — за переменной
 *     может стоять что угодно, гадать хуже, чем оставить прежний вид;
 *  3. итоговая непрозрачность ниже порога — подложка плёнка.
 *
 * `opacity` — плотность пресета подложки блока (поле `BlockBackground.opacity`).
 * Она ПЕРЕМНОЖАЕТСЯ с альфа-каналом кода цвета — ровно так же, как их
 * перемножает сборка самого цвета (`hexToRgba`, blockBackgroundStyle.tsx):
 * замер обязан мерить ту же поверхность, которую рисует стиль. Вызывающие без
 * плотности (пилюля, плашка ошибки, строка варианта) её не передают.
 *
 * Меряется ЛЮБАЯ форма записи, которую понимает `parseCssColorToRgb`
 * (`#rgb`/`#rrggbb`/`#rrggbbaa`, `rgb()/rgba()`, `hsl()`, имена базовых
 * цветов) — та же, что у помощника экрана. Раньше пилюля и плашка сдавались на
 * `rgba(255,255,255,0.95)` и оставляли светлый текст на почти белой заливке.
 */
export function opaqueContrastBaseOf(
  color: string | undefined | null,
  opacity?: number,
): string | null {
  if (typeof color !== 'string') return null;
  const value = color.trim();
  if (value.length === 0) return null;

  const parsed = parseCssColorToRgb(value);
  if (!parsed) return null;

  if (!isOpaqueEnough(parsed.alpha * densityOf(opacity))) return null;

  const byte = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${byte(parsed.r)}${byte(parsed.g)}${byte(parsed.b)}`;
}
